import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Menu from "../Menu";
import useApiUrls from "../../useApiUrls";
import SuccessMessage from "../../components/successMessage";
import FailedMessage from "../../components/FailedMessage";

const AddDefaultHtml = () => {
  const navigate = useNavigate();
  const { getUrlByOperation } = useApiUrls();

  const [groupList, setGroupList] = useState([]);
  const [subGroupList, setSubGroupList] = useState([]);
  const [htmlDocList, setHtmlDocList] = useState([]);
  const [htmlDocZipList, setHtmlDocZipList] = useState([]);

  const [selectedGroupId, setSelectedGroupId] = useState("-1");
  const [selectedSubGroupId, setSelectedSubGroupId] = useState("-1");

  // Selection Mode: "doc" for HTML Document, "zip" for HTML Doc Zip
  const [selectMode, setSelectMode] = useState("doc");
  const [htmlDocId, setHtmlDocId] = useState("0");
  const [htmlDocZipId, setHtmlDocZipId] = useState("0");

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
    return null;
  };

  const token = getCookie("token");
  const userid = getCookie("userid") || "1";

  // Fetch initial data for Groups, HTML Docs, HTML Doc Zips for SubUser
  useEffect(() => {
    const fetchInitialData = async () => {
      // Fetch Groups
      try {
        const groupUrl =
          getUrlByOperation("GroupGetDistinctName") ||
          "https://api.dqr-display-tms.eventum.co.in/api/GroupOperation/SubUser/GetDistinct/name";
        const groupRes = await axios.get(groupUrl, {
          headers: {
            accept: "text/plain",
            UserId: userid,
            "X-Authorization": token,
          },
        });
        if (groupRes.data && groupRes.data.isresponse && Array.isArray(groupRes.data.data)) {
          setGroupList(groupRes.data.data);
        } else if (groupRes.data && Array.isArray(groupRes.data.data)) {
          setGroupList(groupRes.data.data);
        }
      } catch (err) {
        console.error("Error fetching groups:", err);
      }

      // Fetch HTML Documents
      try {
        const docUrl =
          "https://api.dqr-display-tms.eventum.co.in/api/HtmlDocOperation/SubUser/GetDistinct/name";
        const docRes = await axios.get(docUrl, {
          headers: {
            accept: "text/plain",
            UserId: userid,
            "X-Authorization": token,
          },
        });
        if (docRes.data && docRes.data.isresponse && Array.isArray(docRes.data.data)) {
          setHtmlDocList(docRes.data.data);
        } else if (docRes.data && Array.isArray(docRes.data.data)) {
          setHtmlDocList(docRes.data.data);
        }
      } catch (err) {
        console.error("Error fetching html docs:", err);
      }

      // Fetch HTML Doc Zips
      try {
        const zipUrl =
          "https://api.dqr-display-tms.eventum.co.in/api/HtmlDocZipOperation/SubUser/GetDistinct/name";
        const zipRes = await axios.get(zipUrl, {
          headers: {
            accept: "text/plain",
            UserId: userid,
            "X-Authorization": token,
          },
        });
        if (zipRes.data && zipRes.data.isresponse && Array.isArray(zipRes.data.data)) {
          setHtmlDocZipList(zipRes.data.data);
        } else if (zipRes.data && Array.isArray(zipRes.data.data)) {
          setHtmlDocZipList(zipRes.data.data);
        }
      } catch (err) {
        console.error("Error fetching html doc zips:", err);
      }
    };

    fetchInitialData();
  }, [getUrlByOperation, token, userid]);

  // Handle Group Selection change
  const handleGroupChange = async (e) => {
    const groupId = e.target.value;
    setSelectedGroupId(groupId);

    if (groupId === "-1" || groupId === "" || groupId === "0") {
      setSubGroupList([]);
      setSelectedSubGroupId("-1");
    } else {
      setSelectedSubGroupId("-1");
      try {
        const res = await axios.get(
          `https://api.dqr-display-tms.eventum.co.in/api/SubGroupOperation/SubUser/GetDistinct/name/${groupId}`,
          {
            headers: {
              accept: "text/plain",
              UserId: userid,
              "X-Authorization": token,
            },
          }
        );
        if (res.data && res.data.isresponse && Array.isArray(res.data.data)) {
          setSubGroupList(res.data.data);
        } else if (res.data && Array.isArray(res.data.data)) {
          setSubGroupList(res.data.data);
        } else {
          setSubGroupList([]);
        }
      } catch (err) {
        console.error("Error fetching sub-groups:", err);
        setSubGroupList([]);
      }
    }
  };

  // Handle Mode Switch (Doc vs Zip)
  const handleModeChange = (mode) => {
    setSelectMode(mode);
    if (mode === "doc") {
      setHtmlDocZipId("0");
    } else {
      setHtmlDocId("0");
    }
  };

  const handleHtmlDocChange = (e) => {
    const val = e.target.value;
    setHtmlDocId(val);
    if (val !== "0") {
      setSelectMode("doc");
      setHtmlDocZipId("0");
    }
  };

  const handleHtmlDocZipChange = (e) => {
    const val = e.target.value;
    setHtmlDocZipId(val);
    if (val !== "0") {
      setSelectMode("zip");
      setHtmlDocId("0");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    const finalDocId = selectMode === "doc" ? Number(htmlDocId) || 0 : 0;
    const finalZipId = selectMode === "zip" ? Number(htmlDocZipId) || 0 : 0;

    if (finalDocId === 0 && finalZipId === 0) {
      setMessage("Please select either HTML Document or HTML Doc Zip.");
      setMessageType("error");
      setLoading(false);
      return;
    }

    const payload = {
      subgroupid: selectedSubGroupId === "" ? -1 : Number(selectedSubGroupId),
      groupid: selectedGroupId === "" ? -1 : Number(selectedGroupId),
      htmldocid: finalDocId,
      htmldoczipid: finalZipId,
    };

    try {
      const insertUrl =
        getUrlByOperation("DefaultHtmlInsert") ||
        "https://api.dqr-display-tms.eventum.co.in/api/DefaultHtmlOperation/SubUser/Insert";

      const response = await axios.post(insertUrl, payload, {
        headers: {
          accept: "text/plain",
          UserId: userid,
          "X-Authorization": token,
          "Content-Type": "application/json-patch+json",
        },
      });

      if (
        response.data &&
        (response.data.isresponse === true || response.data.responsestatus === "SUCCESS" || response.data.status === 200)
      ) {
        setMessage(response.data?.errormsg || "Default HTML inserted successfully!");
        setMessageType("success");
        setTimeout(() => {
          navigate("/default-html");
        }, 1500);
      } else {
        const errStr =
          response.data?.errormsg ||
          response.data?.message ||
          response.data?.responsestatus ||
          "Failed to insert Default HTML.";
        setMessage(errStr);
        setMessageType("error");
      }
    } catch (error) {
      console.error("Error inserting Default HTML:", error);
      const errStr =
        error.response?.data?.errormsg ||
        error.response?.data?.message ||
        error.message ||
        "Failed to insert Default HTML.";
      setMessage(errStr);
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/default-html");
  };

  const getItemValue = (item) => {
    if (typeof item === "object" && item !== null) {
      return item.id !== undefined ? item.id : item.value !== undefined ? item.value : item.groupid || item.subgroupid || item.htmldocid || item.htmldoczipid || 0;
    }
    return item;
  };

  const getItemLabel = (item) => {
    if (typeof item === "object" && item !== null) {
      return item.value || item.name || item.groupname || item.subgroupname || item.htmldocname || item.id || "Option";
    }
    return item;
  };

  return (
    <>
      <Menu />
      <div className="wrapper">
        <div className="content-wrapper">
          <section className="content-header">
            <div className="container-fluid">
              <div className="row mb-2">
                <div className="col-sm-6">
                  <h1>Add Default HTML</h1>
                </div>
                <div className="col-sm-6">
                  <ol className="breadcrumb float-sm-right">
                    <li className="breadcrumb-item">
                      <a href="/" onClick={(e) => { e.preventDefault(); navigate("/default-html"); }}>
                        Default HTML
                      </a>
                    </li>
                    <li className="breadcrumb-item active">Add</li>
                  </ol>
                </div>
              </div>
            </div>
          </section>

          <section className="content">
            <div className="container-fluid">
              <div className="row">
                <div className="col-md-12">
                  <div className="card card-info">
                    <div className="card-header">
                      <h3 className="card-title">Default HTML Form</h3>
                    </div>
                    <form onSubmit={handleSubmit}>
                      <div className="card-body">
                        {message && messageType === "error" && (
                          <div className="alert alert-danger alert-dismissible">
                            <button type="button" className="close" onClick={() => setMessage("")}>&times;</button>
                            <h5><i className="icon fas fa-ban"></i> Error</h5>
                            {message}
                          </div>
                        )}

                        {/* Group and SubGroup Selection */}
                        <div className="row">
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Group Name</label>
                              <select
                                className="form-control"
                                value={selectedGroupId}
                                onChange={handleGroupChange}
                              >
                                <option value="-1">All</option>
                                {groupList &&
                                  groupList.map((item, idx) => (
                                    <option key={idx} value={getItemValue(item)}>
                                      {getItemLabel(item)}
                                    </option>
                                  ))}
                              </select>
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-group">
                              <label>SubGroup Name</label>
                              <select
                                className="form-control"
                                value={selectedSubGroupId}
                                onChange={(e) => setSelectedSubGroupId(e.target.value)}
                              >
                                <option value="-1">All</option>
                                {selectedGroupId !== "-1" && selectedGroupId !== "" && selectedGroupId !== "0" &&
                                  subGroupList &&
                                  subGroupList.map((item, idx) => (
                                    <option key={idx} value={getItemValue(item)}>
                                      {getItemLabel(item)}
                                    </option>
                                  ))}
                              </select>
                            </div>
                          </div>
                        </div>

                        {/* Document Type Selector (Mutually Exclusive: Document vs Zip) */}
                        <div className="row mt-2 mb-3">
                          <div className="col-md-12">
                            <label className="d-block">Select Document Type (Either HTML Document or HTML Doc Zip)</label>
                            <div className="custom-control custom-radio custom-control-inline">
                              <input
                                type="radio"
                                id="modeDoc"
                                name="docTypeMode"
                                className="custom-control-input"
                                checked={selectMode === "doc"}
                                onChange={() => handleModeChange("doc")}
                              />
                              <label className="custom-control-label" htmlFor="modeDoc">
                                HTML Document
                              </label>
                            </div>
                            <div className="custom-control custom-radio custom-control-inline">
                              <input
                                type="radio"
                                id="modeZip"
                                name="docTypeMode"
                                className="custom-control-input"
                                checked={selectMode === "zip"}
                                onChange={() => handleModeChange("zip")}
                              />
                              <label className="custom-control-label" htmlFor="modeZip">
                                HTML Doc Zip
                              </label>
                            </div>
                          </div>
                        </div>

                        {/* Dropdowns */}
                        <div className="row">
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>HTML Document</label>
                              <select
                                className="form-control"
                                value={selectMode === "doc" ? htmlDocId : "0"}
                                onChange={handleHtmlDocChange}
                                disabled={selectMode !== "doc"}
                              >
                                <option value="0">-- Select HTML Document --</option>
                                {htmlDocList &&
                                  htmlDocList.map((item, idx) => (
                                    <option key={idx} value={getItemValue(item)}>
                                      {getItemLabel(item)}
                                    </option>
                                  ))}
                              </select>
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-group">
                              <label>HTML Doc Zip</label>
                              <select
                                className="form-control"
                                value={selectMode === "zip" ? htmlDocZipId : "0"}
                                onChange={handleHtmlDocZipChange}
                                disabled={selectMode !== "zip"}
                              >
                                <option value="0">-- Select HTML Doc Zip --</option>
                                {htmlDocZipList &&
                                  htmlDocZipList.map((item, idx) => (
                                    <option key={idx} value={getItemValue(item)}>
                                      {getItemLabel(item)}
                                    </option>
                                  ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="card-footer">
                        <button
                          type="submit"
                          className="btn btn-info"
                          disabled={loading}
                        >
                          {loading ? "Submitting..." : "Submit"}
                        </button>
                        <button
                          type="button"
                          className="btn btn-default ml-2"
                          onClick={handleCancel}
                          disabled={loading}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {message && messageType === "success" && (
        <SuccessMessage message={message} onClose={() => setMessage("")} />
      )}
      {message && messageType === "error" && (
        <FailedMessage message={message} onClose={() => setMessage("")} />
      )}
    </>
  );
};

export default AddDefaultHtml;
